import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * PWA install orchestration for RivoNes.
 *
 * Responsibilities:
 *  • Detect device family (ios, android, desktop) + browser + standalone
 *    mode so components render the right CTA.
 *  • Capture the `beforeinstallprompt` event from Chromium browsers so we
 *    can trigger the native prompt on demand from ANY component (the
 *    footer button, the first-visit banner, or an in-flow nudge).
 *  • Provide a single source of truth for "is this device installable and
 *    not yet installed?" — components just read `state.canOffer`.
 *  • Persist first-visit banner dismissal in localStorage so we never
 *    nag returning users.
 *  • Fire the `appinstalled` transition so post-install UI (green ✅
 *    toast + button auto-hide) works without page reload.
 *
 * Why context instead of a plain hook: the `beforeinstallprompt` event
 * fires at most ONCE per page load — if a hook consumer mounts after
 * that, they miss it. By capturing at the root and storing the
 * deferredPrompt in context, every downstream consumer can trigger the
 * native prompt whenever the user asks for it.
 */

const PwaCtx = createContext(null);
const BANNER_DISMISSED_KEY = "rivones_pwa_banner_dismissed_at";
const INSTALLED_HINT_KEY = "rivones_pwa_installed_hint_v1";
const BANNER_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function detectDevice() {
  if (typeof navigator === "undefined") return { family: "unknown", browser: "unknown" };
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isWindows = /Windows NT/.test(ua);
  const isMac = /Mac OS X/.test(ua) && !isIOS;
  const isLinux = /Linux/.test(ua) && !isAndroid;
  const family = isIOS ? "ios" : isAndroid ? "android" : isWindows ? "windows" : isMac ? "macos" : isLinux ? "linux" : "unknown";
  let browser = "unknown";
  if (/CriOS/.test(ua)) browser = "chrome-ios";
  else if (/FxiOS/.test(ua)) browser = "firefox-ios";
  else if (/EdgiOS/.test(ua)) browser = "edge-ios";
  else if (/Version\/[\d.]+.*Safari/.test(ua) && isIOS) browser = "safari";
  else if (/SamsungBrowser/.test(ua)) browser = "samsung";
  else if (/Edg\//.test(ua)) browser = "edge";
  else if (/Chrome\//.test(ua)) browser = "chrome";
  else if (/Firefox\//.test(ua)) browser = "firefox";
  else if (/Safari\//.test(ua)) browser = "safari";
  return { family, browser };
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  // Chromium/Android: matches("(display-mode: standalone)")
  // iOS Safari: navigator.standalone === true
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export function PwaInstallProvider({ children }) {
  const [device] = useState(detectDevice);
  const [standalone, setStandalone] = useState(isStandalone);
  const [installed, setInstalled] = useState(() =>
    isStandalone() || Boolean(typeof localStorage !== "undefined" && localStorage.getItem(INSTALLED_HINT_KEY)),
  );
  const [nativePromptAvailable, setNativePromptAvailable] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [bannerRevealed, setBannerRevealed] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const deferredPromptRef = useRef(null);

  // First-visit banner reveal delay — wait ~7s after mount so the banner
  // doesn't fight for attention on the initial paint.
  useEffect(() => {
    const t = setTimeout(() => setBannerRevealed(true), 7000);
    return () => clearTimeout(t);
  }, []);

  // Capture the Chromium-only beforeinstallprompt so we can trigger it
  // from the footer button on demand.
  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setNativePromptAvailable(true);
    };
    const onInstalled = () => {
      deferredPromptRef.current = null;
      setNativePromptAvailable(false);
      setInstalled(true);
      setShowSuccessToast(true);
      try { localStorage.setItem(INSTALLED_HINT_KEY, "1"); } catch (_) { /* ignore */ }
      // Auto-hide the confirmation after 5s.
      setTimeout(() => setShowSuccessToast(false), 5000);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Track standalone media-query changes (e.g. user launches from home screen mid-session)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia?.("(display-mode: standalone)");
    if (!mq) return;
    const handler = () => setStandalone(mq.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  const promptInstall = useCallback(async () => {
    // iOS Safari has no native prompt → open the visual guide modal.
    if (device.family === "ios") {
      setIosGuideOpen(true);
      return { outcome: "ios-guide" };
    }
    const dp = deferredPromptRef.current;
    if (!dp) {
      // Fallback for browsers that support install but where the event
      // didn't fire (e.g. user rejected once, or Edge menu-install path).
      return { outcome: "unavailable" };
    }
    try {
      dp.prompt();
      const choice = await dp.userChoice;
      deferredPromptRef.current = null;
      setNativePromptAvailable(false);
      return { outcome: choice.outcome }; // "accepted" | "dismissed"
    } catch (_e) {
      return { outcome: "error" };
    }
  }, [device.family]);

  // "Can we offer install right now?" — used by every component that
  // needs to decide whether to render its install affordance.
  const canOffer = useMemo(() => {
    if (installed || standalone) return false;
    if (device.family === "ios") {
      // iOS install is only possible in Safari proper (Chrome iOS etc.
      // are WebKit shells but don't expose Add-to-Home). We still let
      // Chrome iOS see the guide since the user often has Safari
      // available, but flag it so the copy can differ.
      return device.browser === "safari";
    }
    if (device.family === "android" || device.family === "windows" || device.family === "macos" || device.family === "linux") {
      return nativePromptAvailable;
    }
    return false;
  }, [installed, standalone, device, nativePromptAvailable]);

  // First-visit banner eligibility — separate from `canOffer` because we
  // want a cooldown after dismissal + we render a soft nudge even before
  // Chromium fires `beforeinstallprompt` so desktop first-time visitors
  // still see "Vive RivoNes al máximo". If the native prompt isn't
  // available at click time, the banner falls back to the menu hint.
  const bannerEligible = useMemo(() => {
    if (installed || standalone) return false;
    if (device.family === "unknown") return false;
    // iOS Chrome/Edge/Firefox can't install → don't nudge those.
    if (device.family === "ios" && device.browser !== "safari") return false;
    if (!bannerRevealed) return false;
    if (bannerDismissed) return false;
    try {
      const dismissedAt = parseInt(localStorage.getItem(BANNER_DISMISSED_KEY) || "0", 10);
      if (dismissedAt && Date.now() - dismissedAt < BANNER_COOLDOWN_MS) return false;
    } catch (_) { /* ignore */ }
    return true;
  }, [installed, standalone, device, bannerRevealed, bannerDismissed]);

  const dismissBanner = useCallback(() => {
    try { localStorage.setItem(BANNER_DISMISSED_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setBannerDismissed(true);
  }, []);

  const value = useMemo(
    () => ({
      device,
      standalone,
      installed,
      canOffer,
      bannerEligible,
      iosGuideOpen,
      setIosGuideOpen,
      showSuccessToast,
      dismissSuccessToast: () => setShowSuccessToast(false),
      promptInstall,
      dismissBanner,
    }),
    [device, standalone, installed, canOffer, bannerEligible, iosGuideOpen, showSuccessToast, promptInstall, dismissBanner],
  );

  return <PwaCtx.Provider value={value}>{children}</PwaCtx.Provider>;
}

export function usePwaInstall() {
  const ctx = useContext(PwaCtx);
  if (!ctx) {
    // Safe default so components can render without the provider (e.g.
    // during SSR or standalone tests). All flags are off so nothing UI is
    // rendered.
    return {
      device: { family: "unknown", browser: "unknown" },
      standalone: false,
      installed: false,
      canOffer: false,
      bannerEligible: false,
      iosGuideOpen: false,
      setIosGuideOpen: () => {},
      showSuccessToast: false,
      dismissSuccessToast: () => {},
      promptInstall: async () => ({ outcome: "no-provider" }),
      dismissBanner: () => {},
    };
  }
  return ctx;
}
